import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import UserTable from '../components/user/UserTable';
import UserForm from '../components/user/UserForm';
import PasswordChangeForm from '../components/user/PasswordChangeForm';
import FeatureRoleAssignmentModal from '../components/user/FeatureRoleAssignmentModal';
import UserService from '../services/userService';
import { User } from '../types/user';
import { EnhancedUser } from '../types/enhancedUser';
import useAuthStore from '../store/authStore';
import { useTheme } from '../contexts/ThemeContext';

const GroupUserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const { theme } = useTheme();
  const [users, setUsers] = useState<EnhancedUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<EnhancedUser | null>(null);
  const [passwordChangeUser, setPasswordChangeUser] = useState<EnhancedUser | null>(null);
  const [featureAssignmentUser, setFeatureAssignmentUser] = useState<EnhancedUser | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [isPasswordFormOpen, setIsPasswordFormOpen] = useState<boolean>(false);
  const [isFeatureAssignmentOpen, setIsFeatureAssignmentOpen] = useState<boolean>(false);

  // Check if user is admin or group admin
  useEffect(() => {
    if (currentUser && currentUser.role !== 'admin' && currentUser.role !== 'group_admin') {
      navigate('/dashboard');
    }
  }, [currentUser, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await UserService.getAllUsersWithRoles();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('Error loading users. Please try again later.');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = () => {
    setEditingUser(null);
    setIsFormOpen(true);
    setIsPasswordFormOpen(false);
  };

  const handleEditUser = (user: EnhancedUser) => {
    setEditingUser(user);
    setIsFormOpen(true);
    setIsPasswordFormOpen(false);
  };

  const handleChangePassword = (user: EnhancedUser) => {
    setPasswordChangeUser(user);
    setIsPasswordFormOpen(true);
    setIsFormOpen(false);
  };

  const handleDeleteUser = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await UserService.deleteUser(id);
      // Refresh the user list
      fetchUsers();
    } catch (err: any) {
      setError('Error deleting user. Please try again.');
      console.error('Error deleting user:', err);
    }
  };

  const handleAssignFeatures = (user: EnhancedUser) => {
    setFeatureAssignmentUser(user);
    setIsFeatureAssignmentOpen(true);
  };

  const handleFormSubmit = async (userData: Omit<User, 'id'> & { password?: string }) => {
    try {
      // For organization admins, automatically set their organization_id
      if (currentUser && !currentUser.is_super_admin && currentUser.organization_id) {
        userData.organization_id = currentUser.organization_id;
      }

      if (editingUser) {
        // Update existing user
        await UserService.updateUser(editingUser.id, userData);
      } else {
        // Create new user
        if (!userData.password) {
          throw new Error('Password is required for new users');
        }
        await UserService.createUser({
          ...userData,
          password: userData.password
        });
      }

      // Close the form and refresh users
      setIsFormOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Error saving user. Please try again.');
      console.error('Error saving user:', err);
    }
  };

  const handlePasswordChangeSubmit = async (userId: string, password: string) => {
    try {
      await UserService.changePassword(userId, password);
      
      // Close the form and refresh users
      setIsPasswordFormOpen(false);
      setPasswordChangeUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.message || 'Error changing password. Please try again.');
      console.error('Error changing password:', err);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingUser(null);
  };

  const handlePasswordFormCancel = () => {
    setIsPasswordFormOpen(false);
    setPasswordChangeUser(null);
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className={`dashboard-page theme-${theme}`}>
      <Sidebar />
      
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Group User Management</span>
          </div>

          <div className="content-container">
          <div className="page-header">
            <h1>Group User Management</h1>
            <p className="page-description">
              Manage users that you have created. Users you create will be tagged with your name.
            </p>
            <button 
              className="primary-button"
              onClick={handleCreateUser}
              disabled={isFormOpen || isPasswordFormOpen}
            >
              Add New User
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {isFormOpen ? (
            <UserForm 
              user={editingUser}
              currentUserRole={currentUser?.role || 'user'}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          ) : isPasswordFormOpen && passwordChangeUser ? (
            <PasswordChangeForm
              user={passwordChangeUser}
              onSubmit={handlePasswordChangeSubmit}
              onCancel={handlePasswordFormCancel}
            />
          ) : (
            <UserTable
              users={users}
              loading={loading}
              currentUserId={currentUser?.id || null}
              onEdit={handleEditUser}
              onChangePassword={handleChangePassword}
              onDelete={handleDeleteUser}
              onAssignFeatures={handleAssignFeatures}
            />
          )}

          {/* Feature Assignment Modal */}
          {featureAssignmentUser && (
            <FeatureRoleAssignmentModal
              user={featureAssignmentUser}
              isOpen={isFeatureAssignmentOpen}
              onClose={() => setIsFeatureAssignmentOpen(false)}
            />
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupUserManagementPage; 